// src/components/onboarding/StepLocalisation.js
import React, { useState, useEffect, useRef } from 'react';
import { MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { searchCities } from '../../utils/geocoding';

const StepLocalisation = ({ data, onChange, errors }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState(data.city || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      const results = await searchCities(query);
      setSuggestions(results);
      setShowDropdown(results.length > 0);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const selectCity = (s) => {
    setQuery(s.label);
    setShowDropdown(false);
    setSuggestions([]);
    onChange({
      city: s.city,
      cityPostalCode: s.postcode,
      cityLat: s.lat,
      cityLng: s.lng,
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full mx-auto flex items-center justify-center text-white mb-3">
          <MapPin size={28} />
        </div>
        <h2 className="text-lg sm:text-xl font-bold dark:text-gray-100">
          {t('stepLocalisation.title')}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
          {t('stepLocalisation.subtitle')}
        </p>
      </div>

      <div className="relative">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
          {t('stepLocalisation.cityLabel')}
        </label>
        <div className="relative">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
            <MapPin size={18} />
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (data.city) {
                onChange({ city: '', cityPostalCode: '', cityLat: null, cityLng: null });
              }
            }}
            placeholder={t('stepLocalisation.cityPlaceholder')}
            className={`w-full pl-11 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border-2 ${
              errors.city
                ? 'border-red-300'
                : 'border-gray-100 dark:border-gray-700 focus:border-rose-300 dark:focus:border-rose-500'
            } rounded-xl dark:text-gray-100 focus:outline-none transition`}
          />
        </div>

        {showDropdown && suggestions.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden">
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => selectCity(s)}
                className="w-full px-4 py-3 text-left hover:bg-rose-50 dark:hover:bg-gray-800 transition flex items-center gap-2 text-sm dark:text-gray-200"
              >
                <MapPin size={14} className="text-gray-400 flex-shrink-0" />
                <span>{s.label}</span>
              </button>
            ))}
          </div>
        )}

        {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}

        {data.city && (
          <div className="mt-3 p-3 bg-emerald-50 dark:bg-emerald-900/30 rounded-2xl flex items-center gap-2">
            <MapPin size={16} className="text-emerald-600 dark:text-emerald-400" />
            <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
              {data.city}
              {data.cityPostalCode ? ` (${data.cityPostalCode})` : ''}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default StepLocalisation;
